// src/pages/estoque/EstoqueInternoPage.jsx
import React, { useEffect, useMemo, useState } from "react";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";
import { useIsMobile } from "../../hooks/useIsMobile";

import { db } from "../../config/firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  getDocs,
  doc,
  query,
  updateDoc,
  where,
} from "firebase/firestore";

import { formatDataHora } from "../../utils/dateFormat";
import FiltroBar from "../../components/FiltroBar/FiltroBar";
import { pode } from "../../utils/permissoes";

function EstoqueInternoPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const { modoGlobal, empresaAtual } = useEmpresa();
  const isMobile = useIsMobile();

  const podeAlterar = pode(usuarioLogado, "estoque", "estoqueInterno", "altera");
  const podeDeletar = pode(usuarioLogado, "estoque", "estoqueInterno", "deleta");

  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [itens, setItens] = useState([]);

  // filtros
  const [campoFiltro, setCampoFiltro] = useState("produto");
  const [textoFiltro, setTextoFiltro] = useState("");
  const [somenteAbaixoMinimo, setSomenteAbaixoMinimo] = useState(false);

  // modal
  const [modalAberto, setModalAberto] = useState(false);
  const [modoModal, setModoModal] = useState("add"); // add | edit
  const [editandoId, setEditandoId] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [maquinas, setMaquinas] = useState([]);

  // form
  const [empresaId, setEmpresaId] = useState("");
  const [produto, setProduto] = useState("");
  const [codigo, setCodigo] = useState("");
  const [local, setLocal] = useState("");
  const [quantidadeMinima, setQuantidadeMinima] = useState("");
  const [estoque, setEstoque] = useState("");
  const [maquinaId, setMaquinaId] = useState("");

  const tituloPagina = "Estoque Interno";

  // Carregar lista
  useEffect(() => {
    async function carregarEstoque() {
      if (!usuarioLogado?.grupoId) return;

      setCarregando(true);
      setErro("");
      try {
        const filtros = [where("grupoId", "==", usuarioLogado.grupoId)];

        if (!modoGlobal && empresaAtual?.id) {
          filtros.push(where("empresaId", "==", empresaAtual.id));
        }

        const ref = collection(db, "estoqueInterno");
        const q = query(ref, ...filtros);
        const snap = await getDocs(q);
        const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setItens(lista);
      } catch (e) {
        console.error(e);
        setErro("Erro ao carregar estoque interno.");
      } finally {
        setCarregando(false);
      }
    }

    carregarEstoque();
  }, [usuarioLogado?.grupoId, modoGlobal, empresaAtual?.id]);

  // Carregar empresas (para modo global) e máquinas
  useEffect(() => {
    async function carregarBases() {
      if (!usuarioLogado?.grupoId) return;
      try {
        const refEmp = collection(db, "empresas");
        const qEmp = query(refEmp, where("grupoId", "==", usuarioLogado.grupoId));
        const empSnap = await getDocs(qEmp);
        const empLista = empSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", { sensitivity: "base" }));
        setEmpresas(empLista);

        const refMaq = collection(db, "maquinas");
        const qMaq = query(refMaq, where("grupoId", "==", usuarioLogado.grupoId));
        const maqSnap = await getDocs(qMaq);
        const maqLista = maqSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", { sensitivity: "base" }));
        setMaquinas(maqLista);
      } catch (e) {
        console.warn("Falha ao carregar empresas/maquinas:", e);
      }
    }

    carregarBases();
  }, [usuarioLogado?.grupoId]);

  // Preenche empresa default no modal
  useEffect(() => {
    if (!modalAberto) return;
    if (!modoGlobal && empresaAtual?.id) {
      setEmpresaId(empresaAtual.id);
    }
  }, [modalAberto, modoGlobal, empresaAtual?.id]);

  const itensOrdenados = useMemo(() => {
    return [...itens].sort((a, b) => {
      const nomeA = (a.produto || a.modelo || "").toLowerCase();
      const nomeB = (b.produto || b.modelo || "").toLowerCase();
      return nomeA.localeCompare(nomeB);
    });
  }, [itens]);

  const itensFiltrados = useMemo(() => {
    const texto = String(textoFiltro || "").trim().toLowerCase();

    return itensOrdenados.filter((item) => {
      const estoqueVal = Number(item.estoque ?? item.quantidadeAtual ?? 0) || 0;
      const minimoVal = item.quantidadeMinima == null ? null : Number(item.quantidadeMinima);

      if (somenteAbaixoMinimo) {
        if (minimoVal == null) return false;
        if (!(estoqueVal < minimoVal)) return false;
      }

      if (!texto) return true;

      let base = "";
      if (campoFiltro === "produto") base = item.produto || item.modelo || "";
      else if (campoFiltro === "codigo") base = item.codigo || "";
      else if (campoFiltro === "local") base = item.local || "";
      else if (campoFiltro === "maquina") base = item.maquinaNome || item.maquina || "";
      else if (campoFiltro === "empresa") base = item.empresaNome || "";
      else base = "";

      return String(base).toLowerCase().includes(texto);
    });
  }, [itensOrdenados, textoFiltro, campoFiltro, somenteAbaixoMinimo]);

  const maquinasFiltradas = useMemo(() => {
    const eid = modoGlobal ? empresaId : (empresaAtual?.id || empresaId);
    if (!eid) return maquinas;
    return maquinas.filter((m) => !m.empresaId || m.empresaId === eid);
  }, [maquinas, modoGlobal, empresaAtual?.id, empresaId]);

  function resetForm() {
    setProduto("");
    setCodigo("");
    setLocal("");
    setQuantidadeMinima("");
    setEstoque("");
    setMaquinaId("");
    setEditandoId(null);
    setModoModal("add");
  }

  function abrirModalAdicionar() {
    setErro("");
    setModoModal("add");
    setEditandoId(null);
    resetForm();
    setModalAberto(true);
  }

  function abrirModalEditar(item) {
    if (!podeAlterar) {
      alert("Acesso restrito.");
      return;
    }
    if (!item?.id) return;
    setErro("");
    setModoModal("edit");
    setEditandoId(item.id);
    setEmpresaId(item.empresaId || (empresaAtual?.id || ""));
    setProduto(item.produto || item.modelo || "");
    setCodigo(item.codigo || "");
    setLocal(item.local || "");
    setQuantidadeMinima(item.quantidadeMinima != null ? String(item.quantidadeMinima) : "");
    const est = item.estoque ?? item.quantidadeAtual;
    setEstoque(est != null ? String(est) : "");
    setMaquinaId(item.maquinaId || "");
    setModalAberto(true);
  }

  async function excluirItem(item) {
    if (!podeDeletar) {
      alert("Acesso restrito.");
      return;
    }
    if (!item?.id) return;
    const ok = window.confirm(`Excluir o material "${item.produto || item.modelo || item.id}"?`);
    if (!ok) return;

    setErro("");
    try {
      await deleteDoc(doc(db, "estoqueInterno", item.id));
      setItens((prev) => prev.filter((x) => x.id !== item.id));
    } catch (e) {
      console.error(e);
      setErro("Erro ao excluir material.");
    }
  }

  async function salvarMaterial() {
    if (!usuarioLogado?.grupoId) {
      setErro("Usuário sem grupoId. Verifique o cadastro.");
      return;
    }

    const eid = modoGlobal ? empresaId : (empresaAtual?.id || empresaId);
    if (!eid) {
      setErro("Selecione a empresa.");
      return;
    }
    if (!produto.trim()) {
      setErro("Informe o produto.");
      return;
    }

    const emp = empresas.find((e) => e.id === eid);
    const maq = maquinas.find((m) => m.id === maquinaId);

    try {
      setSalvando(true);
      setErro("");

      const payload = {
        grupoId: usuarioLogado.grupoId,
        empresaId: eid,
        empresaNome: emp?.nome || "",

        // campos novos (padrão do print)
        produto: produto.trim(),
        codigo: codigo.trim(),
        local: local.trim(),
        quantidadeMinima: quantidadeMinima === "" ? null : Number(quantidadeMinima),
        estoque: estoque === "" ? null : Number(estoque),
        maquinaId: maquinaId || "",
        maquinaNome: maq?.nome || "",

        // compatibilidade com telas antigas
        modelo: produto.trim(),
        quantidadeAtual: estoque === "" ? 0 : Number(estoque),

        atualizadoEm: new Date(),
        createdAt: new Date(),
      };

      if (modoModal === "edit" && editandoId) {
        await updateDoc(doc(db, "estoqueInterno", editandoId), {
          ...payload,
          atualizadoEm: new Date(),
        });
        setItens((prev) =>
          prev.map((x) => (x.id === editandoId ? { ...x, ...payload, id: editandoId } : x))
        );
      } else {
        const docRef = await addDoc(collection(db, "estoqueInterno"), payload);
        setItens((prev) => [{ id: docRef.id, ...payload }, ...prev]);
      }

      setModalAberto(false);
      resetForm();
    } catch (e) {
      console.error(e);
      setErro(modoModal === "edit" ? "Erro ao salvar alterações." : "Erro ao adicionar material.");
    } finally {
      setSalvando(false);
    }
  }

  const conteudoTabela = (
    <div className="pc-table-wrapper">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, opacity: 0.9 }}>
            <input
              type="checkbox"
              checked={somenteAbaixoMinimo}
              onChange={(e) => setSomenteAbaixoMinimo(e.target.checked)}
            />
            Abaixo do mínimo
          </label>
        </div>
      </div>

      <FiltroBar
        campos={[
          { value: "produto", label: "Produto" },
          { value: "codigo", label: "Código" },
          { value: "local", label: "Local" },
          { value: "maquina", label: "Máquina" },
          { value: "empresa", label: "Empresa" },
        ]}
        campoSelecionado={campoFiltro}
        onCampoChange={setCampoFiltro}
        textoFiltro={textoFiltro}
        onTextoChange={setTextoFiltro}
        onAdicionarClick={null}
        placeholder="Filtrar estoque interno..."
        mostrarAdicionar={false}
      />

      <table className="pc-table">
        <thead>
          <tr>
            <th>Produto</th>
            <th>Código</th>
            <th>Local</th>
            <th>Qtd mínima</th>
            <th>Estoque</th>
            <th>Máquina</th>
            <th>Atualizado em</th>
            <th style={{ width: 110, textAlign: "right" }}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {itensFiltrados.length === 0 && (
            <tr>
              <td colSpan={8} style={{ textAlign: "center", padding: 16 }}>
                Nenhum material cadastrado ainda.
              </td>
            </tr>
          )}
          {itensFiltrados.map((item) => {
            const estoqueVal = Number(item.estoque ?? item.quantidadeAtual ?? 0) || 0;
            const minimoVal = item.quantidadeMinima == null ? null : Number(item.quantidadeMinima);
            const abaixoMinimo = minimoVal != null && estoqueVal < minimoVal;

            return (
            <tr
              key={item.id || `${item.codigo}-${item.produto}`}
              style={abaixoMinimo ? { background: "rgba(234,179,8,0.08)" } : undefined}
            >
              <td>{item.produto || item.modelo || "-"}</td>
              <td>{item.codigo || "-"}</td>
              <td>{item.local || "-"}</td>
              <td>{item.quantidadeMinima ?? "-"}</td>
              <td>{item.estoque ?? item.quantidadeAtual ?? 0}</td>
              <td>{item.maquinaNome || item.maquina || "-"}</td>
              <td>{item.atualizadoEm ? formatDataHora(item.atualizadoEm) : "-"}</td>
              <td style={{ textAlign: "right" }}>
  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
    {podeAlterar && (
      <button
        type="button"
        className="pc-icon-btn"
        title="Editar"
        onClick={() => abrirModalEditar(item)}
      >
        ✏️
      </button>
    )}
    {podeDeletar && (
      <button
        type="button"
        className="pc-icon-btn"
        title="Excluir"
        onClick={() => excluirItem(item)}
      >
        🗑️
      </button>
    )}
  </div>
</td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const modal = modalAberto ? (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) setModalAberto(false);
      }}
    >
      <div
        style={{
          width: "min(720px, 100%)",
          borderRadius: 16,
          background: "var(--cor-card, rgba(15,23,42,0.95))",
          border: "1px solid rgba(255,255,255,0.10)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
          padding: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>
              {modoModal === "edit" ? "Editar material" : "Adicionar material"}
            </div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>
              Cadastro rápido de item para controle interno.
            </div>
          </div>
          <button className="pc-btn-secundario" type="button" onClick={() => setModalAberto(false)}>
            Fechar
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12, marginTop: 14 }}>
          {modoGlobal && (
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 13, opacity: 0.9 }}>Empresa</span>
              <select
                value={empresaId}
                onChange={(e) => setEmpresaId(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.04)",
                  color: "inherit",
                }}
              >
                <option value="" disabled>
                  Selecione...
                </option>
                {empresas.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nome}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label style={{ display: "flex", flexDirection: "column", gap: 6, gridColumn: modoGlobal ? "span 1" : "span 2" }}>
            <span style={{ fontSize: 13, opacity: 0.9 }}>Produto</span>
            <input
              value={produto}
              onChange={(e) => setProduto(e.target.value)}
              placeholder="Ex: Contator tripolar JX2-9511 JNG 220V"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.04)",
                color: "inherit",
              }}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 13, opacity: 0.9 }}>Código</span>
            <input
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="Ex: EM2501"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.04)",
                color: "inherit",
              }}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 13, opacity: 0.9 }}>Local</span>
            <input
              value={local}
              onChange={(e) => setLocal(e.target.value)}
              placeholder="Ex: PM03"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.04)",
                color: "inherit",
              }}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 13, opacity: 0.9 }}>Quantidade mínima</span>
            <input
              type="number"
              value={quantidadeMinima}
              onChange={(e) => setQuantidadeMinima(e.target.value)}
              placeholder="Ex: 1"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.04)",
                color: "inherit",
              }}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 13, opacity: 0.9 }}>Estoque</span>
            <input
              type="number"
              value={estoque}
              onChange={(e) => setEstoque(e.target.value)}
              placeholder="Ex: 2"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.04)",
                color: "inherit",
              }}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6, gridColumn: "span 2" }}>
            <span style={{ fontSize: 13, opacity: 0.9 }}>Máquina</span>
            <select
              value={maquinaId}
              onChange={(e) => setMaquinaId(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.04)",
                color: "inherit",
              }}
            >
              <option value="">(Opcional) Selecionar...</option>
              {maquinasFiltradas.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nome}
                </option>
              ))}
            </select>
          </label>
        </div>

        {erro && (
          <div className="pc-alert pc-alert-erro" style={{ marginTop: 12 }}>
            {erro}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
          <button
            type="button"
            className="pc-btn-secundario"
            onClick={() => {
              setModalAberto(false);
              resetForm();
            }}
          >
            Cancelar
          </button>
          <button type="button" className="pc-btn-primario" onClick={salvarMaterial} disabled={salvando}>
            {salvando ? "Salvando..." : (modoModal === "edit" ? "Salvar" : "Adicionar")}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const conteudo = (
    <div className="pc-page">
      <header className="pc-page-header">
        <div>
          <h1>{tituloPagina}</h1>
          <p className="pc-page-subtitle">
            Controle interno de materiais por empresa e máquina.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button
            type="button"
            className="pc-btn-primario"
            onClick={abrirModalAdicionar}
          >
            + Adicionar material
          </button>
        </div>
      </header>

      {erro && (
        <div className="pc-alert pc-alert-erro" style={{ marginBottom: 12 }}>
          {erro}
        </div>
      )}

      {carregando && <p>Carregando estoque...</p>}
      {!carregando && conteudoTabela}
      {modal}
    </div>
  );

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo={tituloPagina}
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
      >
        <div className="m-form-container">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "0 0 10px 0" }}>
            <div style={{ fontWeight: 700 }}>Materiais</div>
            <button
              type="button"
              className="m-btn m-btn-primary"
              onClick={abrirModalAdicionar}
            >
              + Adicionar
            </button>
          </div>

          {/* Filtros */}
          <div className="m-form-card" style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
              <select
                className="m-form-input"
                value={campoFiltro}
                onChange={(e) => setCampoFiltro(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="produto">Produto</option>
                <option value="codigo">Código</option>
                <option value="local">Local</option>
                <option value="maquina">Máquina</option>
                <option value="empresa">Empresa</option>
              </select>
              <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: "#94a3b8", whiteSpace: "nowrap" }}>
                <input
                  type="checkbox"
                  checked={somenteAbaixoMinimo}
                  onChange={(e) => setSomenteAbaixoMinimo(e.target.checked)}
                />
                Abaixo do mínimo
              </label>
            </div>
            <input
              className="m-form-input"
              placeholder="Filtrar..."
              value={textoFiltro}
              onChange={(e) => setTextoFiltro(e.target.value)}
            />
          </div>

          {erro && <div className="m-alert m-alert-erro">{erro}</div>}

          {carregando ? (
            <p style={{ padding: 16, textAlign: "center" }}>Carregando...</p>
          ) : itensFiltrados.length === 0 ? (
            <p style={{ padding: 16, textAlign: "center", color: "#94a3b8" }}>
              Nenhum material cadastrado.
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {itensFiltrados.map((item) => {
                const estoqueVal = Number(item.estoque ?? item.quantidadeAtual ?? 0) || 0;
                const minimoVal = item.quantidadeMinima == null ? null : Number(item.quantidadeMinima);
                const abaixoMinimo = minimoVal != null && estoqueVal < minimoVal;

                return (
                <div
                  key={item.id || `${item.codigo}-${item.produto}`}
                  className="m-form-card"
                  style={abaixoMinimo ? { borderColor: "rgba(234,179,8,0.35)", background: "rgba(234,179,8,0.06)" } : undefined}
                >
                  <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: 8 }}>
                    {item.produto || item.modelo || "Sem descrição"}
                  </div>
                  <div style={{ fontSize: "0.85rem", color: "#94a3b8", marginBottom: 6 }}>
                    {item.codigo ? `Código: ${item.codigo}` : ""}
                    {item.local ? ` • Local: ${item.local}` : ""}
                  </div>

                  <div className="m-form-row" style={{ marginTop: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "0.75rem", color: "#64748b" }}>Qtd mínima</div>
                      <div style={{ fontWeight: 700, fontSize: "1.05rem" }}>{item.quantidadeMinima ?? "-"}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "0.75rem", color: "#64748b" }}>Estoque</div>
                      <div style={{ fontWeight: 700, fontSize: "1.05rem" }}>{item.estoque ?? item.quantidadeAtual ?? 0}</div>
                    </div>
                  </div>

                  <div style={{ fontSize: "0.8rem", color: "#94a3b8", marginTop: 10 }}>
                    {item.maquinaNome ? `Máquina: ${item.maquinaNome}` : ""}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: 6 }}>
                    Atualizado: {item.atualizadoEm ? formatDataHora(item.atualizadoEm) : "-"}
                  </div>

                  <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
  {podeAlterar && (
    <button className="m-btn-secondary-sm" style={{ flex: 1 }} onClick={() => abrirModalEditar(item)}>
      Editar
    </button>
  )}
  {podeDeletar && (
    <button
      className="m-btn-secondary-sm"
      style={{ flex: 1, color: "#ef4444", borderColor: "rgba(239, 68, 68, 0.3)" }}
      onClick={() => excluirItem(item)}
    >
      Excluir
    </button>
  )}
</div>
                </div>
                );
              })}
            </div>
          )}
        </div>
        {modal}
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop tema={tema} titulo={tituloPagina} subtitulo="">
      {conteudo}
    </LayoutDesktop>
  );
}

export default EstoqueInternoPage;
